interface BottomBarProps {
  nights: number;
  totalPrice: number;
  hasSelection: boolean;
  disabled: boolean;
  /** 查价中：价格区显示 spinner + "价格查询中..."，按钮显示 spinner + "请稍候" */
  priceLoading?: boolean;
  /** 下单中（pre-create 请求中）：按钮显示 spinner + "请稍候"；价格区保持已查到的真实价 */
  bookingLoading?: boolean;
  roomName?: string;
  /** 查价失败提示：非空时价格区显示错误文案（按钮已由 disabled 置灰） */
  priceError?: string | null;
  /** 预订按钮点击回调，由父组件实现 */
  onBook: () => void | Promise<void>;
}

/**
 * 旋转小圆圈：用于按钮 loading 状态。
 * 用纯 CSS（border + animation）实现，避免引入额外图标库。
 */
function Spinner({ color = "#fff", size = 16 }: { color?: string; size?: number }) {
  return (
    <span
      className="inline-block animate-spin rounded-full border-2 border-transparent"
      style={{
        width: size,
        height: size,
        borderTopColor: color,
        borderRightColor: color,
        verticalAlign: "middle",
      }}
    />
  );
}

export default function BottomBar({ nights, totalPrice, hasSelection, disabled, priceLoading, bookingLoading, roomName, priceError, onBook }: BottomBarProps) {

  // 任何加载中（查价 / 下单）都算"忙碌"：关闭点击、显示 spinner
  const busy = !!priceLoading || !!bookingLoading;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white px-5 pt-4 z-40" style={{ boxShadow: "0 -4px 20px rgba(0,0,0,0.06)", paddingTop: "16px", paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}>
      <div className="flex items-center justify-between">
        {hasSelection ? (
          priceError ? (
            // 查价失败：红色错误文案 + 按钮变灰（disabled 由父级控制）
            <div>
              <span className="text-xs text-gray-400">{nights}晚 总价</span>
              <div className="text-xs font-medium mt-1" style={{ color: "#EF4444" }}>{priceError}</div>
            </div>
          ) : priceLoading ? (
            // ★ 查价中：价格区显示 spinner，避免用户看不到反馈而迷茫
            <div>
              <span className="text-xs text-gray-400">{nights}晚 总价</span>
              <div className="flex items-center gap-2 mt-0.5">
                <Spinner color="#9CA3AF" size={14} />
                <span className="text-xs text-gray-400">价格查询中...</span>
              </div>
            </div>
          ) : (
            // 查价成功：显示真实总价（下单中 priceLoading=false，价格正常展示）
            <div>
              <span className="text-xs text-gray-400">{nights}晚 总价</span>
              <div className="flex items-baseline gap-1">
                <span className="font-bold" style={{ color: "#EF4444", fontSize: 26 }}>¥{totalPrice.toLocaleString()}</span>
                <span className="text-xs text-gray-400">起</span>
              </div>
            </div>
          )
        ) : (
          <div>
            <span className="text-sm text-gray-400">请选择房型和入住日期</span>
            <div className="flex items-center gap-2 mt-0.5">
              {!roomName && <span className="text-xs text-gray-300">选择房型 · </span>}
              {roomName && <span className="text-xs font-medium" style={{ color: "#1E3A8A" }}>{roomName}</span>}
            </div>
          </div>
        )}
        <button
          // ★ busy 时不响应 onClick（双重保险），避免查价/下单期间被用户反复点击
          onClick={() => !disabled && !busy && onBook()}
          disabled={disabled || busy}
          className="px-8 py-3 rounded-full text-sm font-bold whitespace-nowrap transition-all flex items-center justify-center gap-2"
          style={{
            // 忙碌中（busy && !disabled）保持橙色，只显示 spinner；
            // 真正 disabled 场景（如业务错误、限流、没选房型）才用灰色禁用态。
            background: disabled ? "#D1D5DB" : "#F97316",
            color: "#fff",
            opacity: disabled ? 0.6 : 1,
            // busy 但未 disabled 时用 wait 光标，给用户"在等待"的反馈
            cursor: disabled || busy ? "wait" : "pointer",
          }}
        >
          {busy ? (
            <>
              <Spinner color="#fff" size={16} />
              <span>请稍候</span>
            </>
          ) : (
            "立即预订"
          )}
        </button>
      </div>
    </div>
  );
}
